import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { getDeliveryUserLabel } from '../../utils/collectDelivery';

const STOCK_DEBOUNCE_MS = 300;

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: '#ffffff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e2e8f0',
      borderWidth: 2,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#7c3aed',
    },
    '&.Mui-focused': {
      boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#7c3aed',
        borderWidth: 2,
      },
    },
  },
};

const CollectDeliveryDialog = ({
  open,
  users,
  selectedUserId,
  badgeInput,
  stockCode,
  cart,
  error,
  loading,
  confirming,
  onClose,
  onUserChange,
  onBadgeInputChange,
  onBadgeScan,
  onStockCodeChange,
  onAddStock,
  onRemoveOrder,
  onConfirm,
  locationLabel,
  userOptional,
  t,
}) => {
  const stockInputRef = useRef(null);
  const stockDebounceRef = useRef(null);
  const latestStockRef = useRef(stockCode || '');
  const selectedUser =
    (users || []).find((deliveryUser) => deliveryUser.id === selectedUserId) || null;
  const cartCount = (cart && cart.length) || 0;
  const canConfirm =
    (userOptional || Boolean(selectedUserId)) && cartCount > 0 && !confirming && !loading;

  useEffect(() => {
    latestStockRef.current = stockCode || '';
  }, [stockCode]);

  useEffect(() => {
    if (open && (userOptional || selectedUserId) && stockInputRef.current) {
      stockInputRef.current.focus();
    }
  }, [open, selectedUserId, userOptional]);

  useEffect(() => {
    return () => {
      if (stockDebounceRef.current) {
        clearTimeout(stockDebounceRef.current);
      }
    };
  }, []);

  const clearStockDebounce = () => {
    if (stockDebounceRef.current) {
      clearTimeout(stockDebounceRef.current);
      stockDebounceRef.current = null;
    }
  };

  const submitStock = (value) => {
    clearStockDebounce();
    const nextValue = value != null ? value : latestStockRef.current;
    if (!String(nextValue || '').trim()) {
      return;
    }
    onAddStock(nextValue);
  };

  const handleBadgeKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (onBadgeScan) {
        onBadgeScan(badgeInput);
      }
    }
  };

  const handleStockCodeInputChange = (event) => {
    onStockCodeChange(event);
    const value = event.target.value;
    latestStockRef.current = value;
    clearStockDebounce();
    if (!String(value || '').trim()) {
      return;
    }
    stockDebounceRef.current = setTimeout(() => {
      stockDebounceRef.current = null;
      submitStock(latestStockRef.current);
    }, STOCK_DEBOUNCE_MS);
  };

  const handleStockKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submitStock(event.target.value);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          maxWidth: 760,
          boxShadow: '0 24px 70px rgba(15, 23, 42, 0.24)',
        },
      }}
    >
      <DialogTitle sx={{ px: 3, py: 2, borderBottom: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.125rem' }}>
              {t('CollectDelivery.Title', { defaultValue: 'Collect / Deliver' })}
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.8125rem', mt: 0.25 }}>
              {t(userOptional ? 'CollectDelivery.SubtitleOptional' : 'CollectDelivery.Subtitle', {
                defaultValue: userOptional
                  ? 'Collect ready {{location}} orders for delivery'
                  : 'Assign ready {{location}} orders to a delivery user',
                location: locationLabel || 'delivery',
              })}
            </Typography>
          </Box>
          <IconButton
            aria-label={t('CollectDelivery.Cancel', { defaultValue: 'Cancel' })}
            onClick={onClose}
            size="small"
            sx={{ color: '#64748b', mt: -0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography sx={{ mb: 0.75, color: '#475569', fontSize: '0.8125rem', fontWeight: 600 }}>
              {t(userOptional ? 'CollectDelivery.DeliveryUserOptional' : 'CollectDelivery.ScanBadgeHint', {
                defaultValue: userOptional
                  ? 'Delivery user (optional)'
                  : 'Scan badge or enter the delivery person',
              })}
            </Typography>
            <Autocomplete
              fullWidth
              options={users || []}
              value={selectedUser}
              inputValue={badgeInput}
              onChange={onUserChange}
              onInputChange={onBadgeInputChange}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : getDeliveryUserLabel(option)
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, state) => {
                const query = String(state.inputValue || '')
                  .trim()
                  .toLowerCase();
                if (!query) return options;
                return options.filter((option) => {
                  const name = getDeliveryUserLabel(option).toLowerCase();
                  const username = String(option.username || '').toLowerCase();
                  const badge = String(option.badge_id || '').toLowerCase();
                  return name.includes(query) || username.includes(query) || badge.includes(query);
                });
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.875rem' }}>
                      {getDeliveryUserLabel(option)}
                    </Typography>
                    <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                      {option.badge_id
                        ? `${option.username || ''} · ${option.badge_id}`
                        : option.username || ''}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  autoFocus={!userOptional}
                  placeholder={t('CollectDelivery.ScanBadgeOrSearch', {
                    defaultValue: 'Scan badge or search',
                  })}
                  variant="outlined"
                  size="small"
                  autoComplete="off"
                  onKeyDown={handleBadgeKeyDown}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <BadgeOutlinedIcon sx={{ color: selectedUser ? '#7c3aed' : '#94a3b8' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />
              )}
            />
            {selectedUser ? (
              <Box
                sx={{
                  mt: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 2,
                  backgroundColor: 'rgba(124, 58, 237, 0.08)',
                  border: '1px solid rgba(124, 58, 237, 0.18)',
                }}
              >
                <Typography sx={{ color: '#5b21b6', fontSize: '0.8125rem', fontWeight: 600 }}>
                  {t('CollectDelivery.SelectedUser', {
                    defaultValue: 'Delivery user: {{name}}',
                    name: getDeliveryUserLabel(selectedUser),
                  })}
                </Typography>
                {selectedUser.badge_id ? (
                  <Typography sx={{ color: '#7c3aed', fontSize: '0.75rem' }}>
                    {selectedUser.badge_id}
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </Box>

          <Box>
            <Typography sx={{ mb: 0.75, color: '#475569', fontSize: '0.8125rem', fontWeight: 600 }}>
              {t('CollectDelivery.ScanStockHint', {
                defaultValue: 'Scan stock label(s)',
              })}
            </Typography>
            <TextField
              fullWidth
              inputRef={stockInputRef}
              placeholder={t('CollectDelivery.ScanStockPlaceholder', {
                defaultValue: 'Scan or enter a stock label, then press Enter',
              })}
              value={stockCode}
              onChange={handleStockCodeInputChange}
              onKeyDown={handleStockKeyDown}
              variant="outlined"
              size="small"
              autoComplete="off"
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#7c3aed' }} />
                  </InputAdornment>
                ),
                endAdornment: loading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={18} sx={{ color: '#7c3aed' }} />
                  </InputAdornment>
                ) : null,
              }}
              sx={fieldSx}
            />
          </Box>
        </Box>

        {error ? (
          <Box
            sx={{
              mt: 2,
              px: 1.5,
              py: 1,
              borderRadius: 2,
              backgroundColor: '#fef2f2',
              color: '#dc2626',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {error}
          </Box>
        ) : null}

        <Box
          sx={{
            mt: 2.5,
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: '#ffffff',
          }}
        >
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead sx={{ backgroundColor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ width: '22%', fontWeight: 700, color: '#475569', fontSize: '0.75rem' }}>
                  {t('CollectDelivery.StockLabel', { defaultValue: 'Stock Label' })}
                </TableCell>
                <TableCell sx={{ width: '28%', fontWeight: 700, color: '#475569', fontSize: '0.75rem' }}>
                  {t('CollectDelivery.PatientName', { defaultValue: 'Patient Name' })}
                </TableCell>
                <TableCell sx={{ width: '12%', fontWeight: 700, color: '#475569', fontSize: '0.75rem' }}>
                  {t('CollectDelivery.RxQty', { defaultValue: 'Rx Qty' })}
                </TableCell>
                <TableCell sx={{ width: '24%', fontWeight: 700, color: '#475569', fontSize: '0.75rem' }}>
                  {t('CollectDelivery.StorageLocation', { defaultValue: 'Storage Location' })}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ width: '14%', fontWeight: 700, color: '#475569', fontSize: '0.75rem' }}
                >
                  {t('CollectDelivery.Action', { defaultValue: 'Action' })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cartCount > 0 ? (
                cart.map((order) => (
                  <TableRow
                    key={order.id}
                    sx={{
                      '&:nth-of-type(even)': { backgroundColor: '#f8fafc' },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700, color: '#7c3aed' }}>
                      {order.stock_code || '-'}
                    </TableCell>
                    <TableCell sx={{ color: '#0f172a' }}>
                      {`${order.first_name || ''} ${order.last_name || ''}`.trim() || '-'}
                    </TableCell>
                    <TableCell>{order.rx_qty || '-'}</TableCell>
                    <TableCell sx={{ color: '#475569' }}>
                      {order.storage_location || order.room_number || '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        onClick={() => onRemoveOrder(order.id)}
                        sx={{
                          textTransform: 'none',
                          minWidth: 0,
                          px: 1,
                          fontWeight: 600,
                          color: '#2563eb',
                          '&:hover': {
                            backgroundColor: 'rgba(37, 99, 235, 0.08)',
                          },
                        }}
                      >
                        {t('CollectDelivery.RemoveOrder', { defaultValue: 'Remove' })}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 7, color: '#94a3b8' }}>
                    {t('CollectDelivery.EmptyCart', {
                      defaultValue: 'Scan a stock label or click a Ready order to add it.',
                    })}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0', gap: 1.5 }}>
        <Button
          onClick={onClose}
          sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b', mr: 'auto' }}
        >
          {t('CollectDelivery.Cancel', { defaultValue: 'Cancel' })}
        </Button>
        <Button
          variant="contained"
          disabled={!canConfirm}
          onClick={onConfirm}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            px: 2.75,
            py: 1,
            backgroundColor: '#7c3aed',
            boxShadow: '0 6px 18px rgba(124, 58, 237, 0.25)',
            '&:hover': {
              backgroundColor: '#6d28d9',
            },
            '&.Mui-disabled': {
              backgroundColor: '#e2e8f0',
              color: '#94a3b8',
              boxShadow: 'none',
            },
          }}
        >
          {confirming
            ? t('CollectDelivery.Confirming', { defaultValue: 'Confirming…' })
            : t('CollectDelivery.Confirm', { defaultValue: 'Confirm Delivery' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CollectDeliveryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  users: PropTypes.array,
  selectedUserId: PropTypes.string,
  badgeInput: PropTypes.string,
  stockCode: PropTypes.string,
  cart: PropTypes.array,
  error: PropTypes.string,
  loading: PropTypes.bool,
  confirming: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onUserChange: PropTypes.func.isRequired,
  onBadgeInputChange: PropTypes.func.isRequired,
  onBadgeScan: PropTypes.func,
  onStockCodeChange: PropTypes.func.isRequired,
  onAddStock: PropTypes.func.isRequired,
  onRemoveOrder: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  locationLabel: PropTypes.string,
  userOptional: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

CollectDeliveryDialog.defaultProps = {
  users: [],
  selectedUserId: '',
  badgeInput: '',
  stockCode: '',
  cart: [],
  error: '',
  loading: false,
  confirming: false,
  onBadgeScan: undefined,
  locationLabel: '',
  userOptional: false,
};

export default CollectDeliveryDialog;



// WEBPACK FOOTER //
// ./src/components/collect-delivery/CollectDeliveryDialog.jsx